import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import 'firebase/compat/functions';
import 'firebase/compat/app-check';
import 'firebase/compat/analytics';
import { getMultiFactorResolver as firebaseGetMultiFactorResolver } from 'firebase/auth';
import moment from 'moment';
import * as Sentry from '@sentry/browser';
// import { intercomInit } from '../variableModules/intercom.module';
// import { appcuesInit } from '../variableModules/appcues.module';
import { signInWithEmailAndPassword } from '../variableModules/auth.module';
// import { tosCheckFunction } from '../variableModules/tosCheck.module';
import * as coreui from '@coreui/coreui-pro';
import Swal from 'sweetalert2';
import versionJSON from './version.json';
// import ComponentLoader from '../variableModules/ComponentLoader';
import '@awesome.me/kit-269fbef2e2/icons/js/all.min.js'; // import fontawesome globally so fa-icons work in the html

// import '../prerender-helper.js'; // Import prerender helper for site-wide usage

//-- WebSocket for build notifications ----------------------------------------
// Check if we should connect to WebSocket (in localhost:5002)
const shouldConnectWebSocket = () => {
  if (!isLocalhost()) return false;

  const port = window.location.port;
  if (port === '5002') return true;
};

const connectWebSocket = () => {
  if (!shouldConnectWebSocket()) return null;

  const WS_PORT = 3456;
  try {
    const ws = new WebSocket(`ws://localhost:${WS_PORT}`);

    ws.onopen = () => {
      console.log('🔌 Connected to build notification service');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📢 Build notification received:', data);

        if (data.type === 'build-complete' && data.success) {
          console.log('🔄 Reloading configuration due to build completion');
          reloadConfiguration();
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    };


    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket connection closed. Attempting to reconnect in 5 seconds...');
      setTimeout(connectWebSocket, 5000);
    };

    return ws;

  } catch (error) {
    console.error('Error creating WebSocket connection:', error);
    setTimeout(connectWebSocket, 5000);
    return null;
  }
};

// Function to reload configuration after build
const reloadConfiguration = () => {
  console.log('🔄 Reloading configuration...');

  try {
    // Notify the application that configuration has been reloaded
    window.dispatchEvent(new CustomEvent('config-reloaded', {
      detail: { timestamp: Date.now() }
    }));

    console.log('✅ Configuration reload complete');
    window.location.reload(true);
  } catch (error) {
    console.error('Error during configuration reload:', error);
    Swal.fire({
      title: 'Configuration Reload Error',
      text: 'An error occurred while reloading the configuration. You may need to refresh the page.',
      icon: 'warning',
      confirmButtonText: 'Okay',
    });
  }
};

//-- Helper Functions -----------------------------------------------------------
const isLocalhost = () =>
  window.location.host.includes('localhost') ||
  window.location.host.includes('127.0.0.1') ||
  window.location.host.includes('.local');

const initFirebaseApp = () => {
  const firebaseConfig = {
    apiKey: `${import.meta.env.VITE_apiKey}`,
    authDomain: `${import.meta.env.VITE_authDomain}`,
    projectId: `${import.meta.env.VITE_projectId}`,
    storageBucket: `${import.meta.env.VITE_storageBucket}`,
    messagingSenderId: `${import.meta.env.VITE_messagingSenderId}`,
    appId: `${import.meta.env.VITE_appId}`,
    measurementId: `${import.meta.env.VITE_measurementId}`,
  };

  console.log(firebaseConfig);
  // Only initialize if not already initialized
  if (!firebase.apps.length) {
    firebase.initializeApp(firebaseConfig);
  }
  return firebaseConfig;
};

const initAppCheck = (app, recaptchaKey) => {
  // Skip App Check entirely on localhost - it requires debug tokens registered per project
  if (isLocalhost()) {
    console.log('[AppCheck] Skipping App Check on localhost');
    return null;
  }

  try {
    const appCheck = firebase.appCheck();
    const provider = new firebase.appCheck.ReCaptchaEnterpriseProvider(recaptchaKey);
    appCheck.activate(provider, true);
    return appCheck;
  } catch (error) {
    console.warn('[AppCheck] Failed to initialize App Check:', error.message);
    console.warn('[AppCheck] The app will continue without App Check protection.');
    return null;
  }
};

const initEmulators = (functions, functionsUS, auth, db, storage) => {
  if (isLocalhost()) {
    functions.useEmulator('localhost', 5001);
    functionsUS.useEmulator('localhost', 5001);
    // auth.useEmulator('http://localhost:9099');
    // db.useEmulator('localhost', 8080);
    // storage.useEmulator('localhost', 9199);
  }
};

const getSentryDSN = () => {
  // Default to prod DSN
  let dsn = "https://bf894d60308b3ec204aee45b8c837de3@o4506330372702208.ingest.us.sentry.io/4506697576087552";
  const hostname = window?.location?.hostname || "";

  if (
    hostname.includes("deiterate-hybrid.web.app") || hostname.includes("hybrid.deit.dev") ||
    hostname === "127.0.0.1" ||
    hostname === "localhost"
  ) {
    // Hybrid environment
    dsn = "https://8881db6ea090645c46678d21e9ceb3de@o4506330372702208.ingest.us.sentry.io/4509437092888576";
  } else if (hostname.includes("admin.deit.dev")) {
    // UAT environment
    dsn = "https://5b746c4b18d3d62bbd42c963754fc310@o4506330372702208.ingest.us.sentry.io/4506334421516288";
  } else if (hostname.includes("admin.deiterate.com")) {
    // Production environment
    dsn = "https://bf894d60308b3ec204aee45b8c837de3@o4506330372702208.ingest.us.sentry.io/4506697576087552";
  }
  return dsn;
};

const initSentry = (sentryEnabled) => {
  // We need to swap DSN between hybrid, uat and prod
  // hybrid is on deiterate-hybrid.web.app and 127.0.0.1
  // uat is on admin.deit.dev
  // prod is on admin.deiterate.com

  const dsn = sentryEnabled ? getSentryDSN() : "";
  const environment = window?.location?.hostname?.includes("hybrid") ? "hybrid" : window?.location?.hostname?.includes("deit.dev") ? "uat" : "prod";
  if (dsn && sentryEnabled) {
    Sentry.init({
      dsn,
      release: `deiterate-${environment}@${versionJSON.version}`,
      integrations: [
        Sentry.browserTracingIntegration,
        Sentry.replayIntegration,
        Sentry.feedbackIntegration({
          colorScheme: "system",
          isNameRequired: true,
          isEmailRequired: true,
          autoInject: false,
          showReportDialog: false,
        })
      ],
      sendDefaultPii: true,
      tracesSampleRate: 1.0,
      tracePropagationTargets: [
        /^https:\/\/admin\.deiterate\.com/,
        /^https:\/\/login\.deiterate\.com/,
        /^https:\/\/ssr-test\.deiterate\.com/,
        /^https:\/\/ssr-test\.deit\.dev/,
        /^https:\/\/admin\.deit\.dev/,
        /^https:\/\/deiterate-hybrid\.web\.app/,
        /^https:\/\/hybrid\.deit\.dev/,
      ],
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event, hint) {
        Sentry.setUser({
          id: window.currentUser?.userID || "unknown",
          email: window.currentUser?.email || "unknown",
          username: window.currentUser?.firstName + ' ' + window.currentUser?.lastName || "unknown",
        });
        event.contexts = {
          ...event.contexts,
          "Company Info": {
            companyID: window.companyID || "unknown",
            companyName: window.companyName || "unknown",
          },
          user: {
            ...event.contexts?.user,
            email: window.currentUser?.email || "unknown",
            firstName: window.currentUser?.firstName || "unknown",
            lastName: window.currentUser?.lastName || "unknown",
            roles: window.currentUser?.roles || "unknown",
            team: window.currentUser?.team || "unknown",
            businessUnit: window.currentUser?.businessUnit || "unknown",
            userID: window.currentUser?.id || "unknown"
          }
        };
        return event;
      },
    });
  }
};

const setCloudFunctionURL = () => {
  // const localURL = 'http://localhost:5001/deiterate-hybrid/australia-southeast1';
  // if (isLocalhost()) return localURL;
  return import.meta.env.VITE_cloudFunctionURL;
};

const setAPIURL = () => {
  // const localURL = 'http://localhost:5001/deiterate-hybrid/australia-southeast1/deiterateInternalAPI';
  // if (isLocalhost()) return localURL;
  return import.meta.env.VITE_cloudFunctionURL + '/deiterateInternalAPI';
};

//-- Main Initialization --------------------------------------------------------
let firebaseConfig = {};
let sentryEnabled = import.meta.env.MODE !== 'development';
let appCheck = null;
let recaptchaKey = import.meta.env.VITE_recaptchaKey;
let publicKey = import.meta.env.VITE_publicKey;

let app = null;
let auth = null;
let analytics = null;
let db = null;
let firebaseAuth = null;
let functions = null;
let functionsUS = null;
let storage = null;
let storageRef = null;
let FirebaseFieldPath = null;
let FirebaseFieldValue = null;
let FirebaseTimestamp = null;

try {
  // Global dependencies
  //window.ComponentLoader = new ComponentLoader();
  window.coreui = coreui;
  window.Swal = Swal;

  firebaseConfig = initFirebaseApp();

  // For local development, disable Sentry if needed.
  // if (import.meta.env.MODE === 'development') {
  //   sentryEnabled = false;
  // }

  // Set up Firebase services
  app = firebase.app();
  auth = firebase.auth(app);
  
  // Firestore uses the default database in whichever project is configured
  // Region is determined by VITE_projectId (eu-deiterate vs deiterate-hybrid)
  db = firebase.firestore(app);
  db.settings({ ignoreUndefinedProperties: true, merge: true });
  
  firebaseAuth = firebase.auth;
  const isEU = import.meta.env.VITE_REGION === 'eu';
  functions = isEU ? app.functions('europe-west4') : app.functions('australia-southeast1');
  functionsUS = app.functions('us-central1');
  storage = firebase.storage(app);
  storageRef = storage.ref();
  FirebaseFieldPath = firebase.firestore.FieldPath;
  FirebaseFieldValue = firebase.firestore.FieldValue;
  FirebaseTimestamp = firebase.firestore.Timestamp;

  // Set auth persistence to local
  auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);

  // Initialize analytics if not on localhost
  if (!isLocalhost()) {
    analytics = firebase.analytics(app);
  }

  // Initialize App Check
  appCheck = initAppCheck(app, recaptchaKey);

  // Set global Cloud Function URL
  window.cloudFunctionURL = setCloudFunctionURL();

  // Set global API URL
  window.apiURL = setAPIURL();

  // Initialize emulators if in local environment
  // initEmulators(functions, functionsUS, auth, db, storage);

  // Initialize Sentry
  initSentry(sentryEnabled);

  // Expose globals and initialize additional modules
  window.global ||= window;
  window.signInWithEmailAndPassword = signInWithEmailAndPassword;
  // appcuesInit();
  // intercomInit();
  // tosCheckFunction();

  // Initialize prerender helper with debug mode when appropriate
  if (window.prerenderHelper) {
    // Enable debug mode only in development
    window.prerenderHelper.setDebug(import.meta.env.MODE === 'development');
    console.log('✅ Prerender helper initialized');
  }

  // Connect to build notification service if in localhost environment
  // Add a small delay to ensure the page has loaded properly
  if (shouldConnectWebSocket()) setTimeout(() => {
    const wsConnection = connectWebSocket();
    window.__buildNotificationWs = wsConnection; // Store for debugging
  }, 1000);
} catch (error) {
  console.error('Error during initialization:', error);
  if (Swal) {
    Swal.fire({
      title: 'Error during initialization, please refresh the page.',
      text: error.toString(),
      icon: 'error',
      confirmButtonText: 'Okay',
    });
  }
}

//-- Banner Injection -----------------------------------------------------------
// const injectBanner = () => {
//   const showBanner = () => {
//     const isSmallScreen = window.innerWidth <= 1137; // Adjust the width as needed
//     const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
//     const bannerShown = sessionStorage.getItem('bannerShown');
//     const path = window.location.pathname;

//     console.log('Banner shown:', bannerShown);

//     if (!bannerShown) {
//       let message = '';
//       if (isSmallScreen && isFirefox && path !== '/account_management') {
//         message =
//           'de.iterate may not function as expected on small screens in Firefox.';
//       } else if (isSmallScreen && path !== '/account_management') {
//         message = 'de.iterate may not function as expected on small screens.';
//       } else if (isFirefox) {
//         message = 'de.iterate may not function as expected in Firefox.';
//       }

//       if (message) {
//         Swal.fire({
//           title: 'Warning',
//           text: message,
//           icon: 'warning',
//           confirmButtonText: 'Okay',
//         }).then(() => {
//           sessionStorage.setItem('bannerShown', 'true');
//         });
//       }
//     }
//   };

//   window.addEventListener('load', showBanner);
//   window.addEventListener('resize', showBanner);
// };

// window.addEventListener('DOMContentLoaded', injectBanner);

//-- Authentication State Handling ----------------------------------------------
let tokenRefreshInterval;

// Utility to generate/persist a sessionId (UUID) per tab/device
function getOrCreateSessionId() {
  let sessionId = localStorage.getItem('sessionId');
  if (!sessionId) {
    // Use crypto.randomUUID if available, fallback to a simple uuid
    sessionId = (window.crypto?.randomUUID?.() || ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)));
    localStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
}

function getDeviceInfo() {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    // Optionally add more info if needed
  };
}

async function updateSessionCookie() {
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken(true);
      const uid = currentUser.uid;
      const sessionToken = `${token}--__--${uid}`;
      const sessionId = getOrCreateSessionId();
      const deviceInfo = getDeviceInfo();

      // const response = await fetch('/auth/refresh-session', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ token: sessionToken, sessionId, deviceInfo }),
      //   credentials: 'include'
      // });

      // if (response.status === 401) {
      //   // Session is invalid, sign out and redirect to login
      //   await auth.signOut();
      //   if (window.location.pathname !== '/login') {
      //     window.location.href = '/login';
      //   }
      //   return false;
      // }

      // if (!response.ok) {
      //   console.error('Failed to update session cookie');
      //   return false;
      // }
      // // Optionally persist sessionId if backend returns a new one
      // const data = await response.json();
      // if (data.sessionId && data.sessionId !== sessionId) {
      //   localStorage.setItem('sessionId', data.sessionId);
      // }
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error updating session cookie:', error);
    // Optionally sign out here too
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return false;
  }
}

// Set up token refresh before expiration
function setupTokenRefresh() {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
  }
  // Refresh token every 45 minutes (before the 1-hour expiration)
  tokenRefreshInterval = setInterval(updateSessionCookie, 45 * 60 * 1000);
}

auth.onAuthStateChanged(async (user) => {
  if (user) {
    window.__DebugUser = user;
    if (!isLocalhost()) {
      // Set up token refresh and cookie via backend
      await updateSessionCookie();
      setupTokenRefresh();
    }
    // No longer set __session_react cookie directly; backend sets it centrally
    // SessionId is generated/persisted in updateSessionCookie
  }
});

//-- Exports --------------------------------------------------------------------
export {
  firebaseConfig,
  app,
  auth,
  analytics,
  db,
  firebase as firebaseImport,
  functions,
  storage,
  storageRef,
  FirebaseFieldValue,
  FirebaseTimestamp,
  FirebaseFieldPath,
  firebaseAuth,
  moment,
  publicKey,
  functionsUS,
  firebaseGetMultiFactorResolver,
};